import React, { useContext, useEffect, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { PostCallWithErrorResponse, simpleGetCallWithErrorResponse } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';
import { ToastMsg } from '../ChatAdmin/toastmsg';

const ReclassificationToolPage = () => {
  const { sidebar } = useContext(AppContext);

  const initialTrans = {
    SourceLedgerId: '',
    DestinationLedgerId: '',
    TransactionId: '',
    date: new Date().toISOString().split('T')[0],
    reason: '',
  };

  const [formData, setFormData] = useState(initialTrans);
  const [isLoading, setIsLoading] = useState(false);
  const [transectionList, setTransectionList] = useState([]);
  const [lederList, setLedgerList] = useState([]);

  const getLedgerList = () => {
    simpleGetCallWithErrorResponse(ApiConfig.GET_LEDGER_LIST).then((res) => {
      if (res?.json?.Success) setLedgerList(res?.json?.Data);
    });
  };

  useEffect(() => {
    getLedgerList();
  }, []);

  const getTranssectionList = (id) => {
    PostCallWithErrorResponse(ApiConfig.GET_MASTER_TRANSECTION + id, JSON.stringify({})).then((res) => {
      if (res?.json?.Success) setTransectionList(res?.json?.Data);
    });
  };

  const handleSubmit = () => {
    setIsLoading(true);
    PostCallWithErrorResponse(ApiConfig.LEDGER_RECLASSIFICATION, JSON.stringify(formData)).then((res) => {
      if (res?.json?.Success) {
        ToastMsg('success', res?.json?.Message);
        setFormData(initialTrans);
        setTransectionList([]);
      } else {
        ToastMsg('error', res?.json?.Message);
      }
      setIsLoading(false);
    });
  };

  return (
    <main className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      <div id="sales-main">
        <section className="cx-inneer-header">
          <div className="main-inner">
            <div className="left">
              <label>Reclassification Tool</label>
            </div>
          </div>
        </section>

        <section className="mt-3">
          <div className="card border p-4" style={{ borderRadius: '12px' }}>
            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3">
              <div className="p-2 rounded" style={{ backgroundColor: '#e0f7fa', color: '#00acc1' }}>
                <RefreshCcw size={24} />
              </div>
              <div>
                <h4 className="mb-1">Expense Reclassification</h4>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Move costs from one GL account to another.</p>
              </div>
            </div>

            <div className="row g-4">
              {/* Source — Credit/Decrease */}
              <div className="col-lg-4">
                <div className="p-4 border rounded h-100 d-flex flex-column" style={{ backgroundColor: '#fff5f5', borderColor: '#feb2b2' }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0 text-uppercase" style={{ fontSize: '0.75rem', color: '#c53030' }}>Move From (Credit)</h6>
                    <span className="badge border" style={{ backgroundColor: '#ffffff', color: '#c53030' }}>- CR</span>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Source Account</label>
                    <select
                      className="form-select bg-white"
                      value={formData.SourceLedgerId}
                      onChange={(e) => {
                        getTranssectionList(Number(e.target.value));
                        setFormData({ ...formData, SourceLedgerId: Number(e.target.value) });
                      }}
                    >
                      <option value="">Select Account...</option>
                      {lederList.map((acc, i) => <option key={i} value={acc.Id}>{acc.Name} - {acc?.PrimaryGroup}</option>)}
                    </select>
                  </div>
                  <div className="mt-auto p-2 rounded" style={{ backgroundColor: 'rgba(197,48,48,0.1)', color: '#c53030', fontSize: '0.75rem' }}>
                    <strong>Impact:</strong> Decreases balance.
                  </div>
                </div>
              </div>

              {/* Destination — Debit/Increase */}
              <div className="col-lg-4">
                <div className="p-4 border rounded h-100 d-flex flex-column" style={{ backgroundColor: '#f0fff4', borderColor: '#9ae6b4' }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0 text-uppercase" style={{ fontSize: '0.75rem', color: '#2f855a' }}>Move To (Debit)</h6>
                    <span className="badge border" style={{ backgroundColor: '#ffffff', color: '#2f855a' }}>+ DR</span>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Destination Account</label>
                    <select
                      className="form-select bg-white"
                      value={formData.DestinationLedgerId}
                      onChange={(e) => setFormData({ ...formData, DestinationLedgerId: Number(e.target.value) })}
                    >
                      <option value="">Select Account...</option>
                      {lederList.map((acc, i) => <option key={i} value={acc.Id}>{acc.Name} - {acc?.PrimaryGroup}</option>)}
                    </select>
                  </div>
                  <div className="mt-auto p-2 rounded" style={{ backgroundColor: 'rgba(47,133,90,0.1)', color: '#2f855a', fontSize: '0.75rem' }}>
                    <strong>Impact:</strong> Increases balance.
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="col-lg-4">
                <div className="p-4 border rounded h-100 d-flex flex-column bg-white">
                  <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: '0.75rem' }}>Transaction Details</h6>
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Transfer Account</label>
                    <select
                      className="form-select bg-white"
                      value={formData.TransactionId}
                      onChange={(e) => setFormData({ ...formData, TransactionId: Number(e.target.value) })}
                    >
                      <option value="">Select Account...</option>
                      {transectionList.map((acc, i) => (
                        <option key={i} value={acc.TransactionId}>{`Cr ${acc?.CrAmount} - Dr ${acc?.DrAmount}`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3 flex-grow-1">
                    <label className="form-label" style={{ fontSize: '0.875rem' }}>Reason</label>
                    <textarea
                      className="form-control form-control-sm"
                      style={{ height: '80px', resize: 'none' }}
                      placeholder="Explanation for audit trail..."
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    />
                  </div>
                  <div className="d-flex gap-2 mt-auto">
                    <button
                      className="btn btn-outline-secondary w-50"
                      onClick={() => setFormData({ ...initialTrans })}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-primary w-50"
                      disabled={(!formData.TransactionId || !formData.SourceLedgerId || !formData.DestinationLedgerId) || isLoading}
                      onClick={handleSubmit}
                    >
                      Process
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ReclassificationToolPage;
